/**
 * ReplayEngine — REN kjerne for reise-replay (Fase 0-spike → vokser i Fase 1).
 *
 * ARKITEKTURREGEL (eng-review D3): denne modulen skal ALDRI importere
 * firebase/leaflet/capacitor/react eller andre moduler med sideeffekter.
 * Regelen håndheves av test/replayEngine.imports.test.ts (vaktpost).
 * All tegning/IO skjer i ReplayCanvas-laget — motoren regner bare.
 *
 *   TripSpec ──buildTimeline──► ReplayTimeline ──frameState(t)──► FrameState
 *                                    │
 *                                    └─buildTileManifest──► fliser å preloade
 *
 * Koordinatsystem: «world» = Web Mercator normalisert til [0,1]² (z=0-flisen).
 * Skjermtransform gjøres av kalleren ut fra FrameState.center + zoom.
 */

import type { SpotVisit } from '../types';

// ── Typer ────────────────────────────────────────────────────────────────────

/** Brukervalgt medieinnslag på et stopp (Fase 3 «Komponer selv»). Kun lokale
 *  object-URL-er i v1 — ingenting lastes opp; mediet lever i eksportfila. */
export interface StopMedia {
  kind: 'photo' | 'video';
  url: string;
  /** For video: brukt kliplengde i sekunder (kappes av stoppets varighet). */
  durationSec?: number;
}

export interface ReplayStop {
  spotId: string;
  name: string;
  latitude: number;
  longitude: number;
  visitedAt: number;      // ms epoch
  checkedOutAt?: number;  // ms epoch — mangler → én-dags-besøk
  photoUrl?: string;
  county?: string;
  /** Komponer-modus: egne bilder/videoklipp som vises i fullskjerm under stoppet. */
  media?: StopMedia[];
}

/** Mellomspill: medier som vises MELLOM to stopp (tatt underveis på etappen). */
export interface TripInterlude {
  afterStopIndex: number; // vises etter kjøreetappen fra dette stoppet
  media: StopMedia[];
}

export interface TripSpec {
  stops: ReplayStop[]; // kronologisk sortert, kun stopp med gyldige koordinater
  interludes?: TripInterlude[];
  /**
   * Faktiske vei-ruter per etappe (fra stop i → i+1), lat/lon-punkter — hentet
   * fra OSRM ved forhåndslasting og bakt inn så eksporten forblir offline.
   * legRoutes[i] mangler/tom → Bezier-fallback for den etappen (off-road).
   */
  legRoutes?: Array<GeoPoint[] | undefined>;
}

export interface WorldPoint { x: number; y: number }

/** Kubisk Bezier i world-koordinater. */
export interface LegCurve { p0: WorldPoint; c1: WorldPoint; c2: WorldPoint; p1: WorldPoint }

export type SegmentKind = 'stop' | 'drive' | 'interlude';

export interface TimelineSegment {
  kind: SegmentKind;
  startSec: number;
  durationSec: number;
  /** Indeks i spec.stops: stopp → stoppet; drive/interlude → FRA-stoppet. */
  stopIndex: number;
  curve?: LegCurve; // kun drive (fallback + bearing når ingen vei-rute)
  /** Vei-polylinje i world-koord (kun drive) — kjøretøyet følger denne når satt. */
  routePath?: WorldPoint[];
  /** Per-etappe fit-zoom (drive/interlude) — kameraet zoomer aldri lenger ut enn dette. */
  driveZoomFit?: number;
  interludeMedia?: StopMedia[]; // kun interlude
}

export interface ReplayTimeline {
  spec: TripSpec;
  segments: TimelineSegment[];
  totalSec: number;
  driveZoom: number;
  stopZoom: number;
}

export interface FrameState {
  center: WorldPoint;
  zoom: number;               // kan være fraksjonell
  /**
   * Flis-ANKER for denne framen: fliser hentes/tegnes KUN på dette
   * heltallsnivået, skalert med 2^(zoom - anchorZoom). Segmentbasert
   * (stopp → stopZoom-nivå, kjøring → driveZoom-nivå) — empirisk målt i
   * Fase 0-spiken som klart billigst (216 fliser mot 392–708 for
   * floor/round/nearest-strategier på en 3-stopps tur).
   */
  anchorZoom: number;
  vehicle: { pos: WorldPoint; bearingDeg: number; visible: boolean };
  activeStopIndex: number | null; // satt under stopp-segment
  stopCardOpacity: number;    // 0..1
  /** Lokal fremdrift 0..1 innen aktivt stopp-segment (0 under kjøring) —
   *  driver lysbildeskift/video-tid i Komponer-modus. Deterministisk. */
  stopLocalProgress01: number;
  /** Satt under mellomspill (medier mellom stopp): innslagene + lokal fremdrift. */
  interlude?: { media: StopMedia[]; local01: number };
  progress01: number;         // total fremdrift
  /** Andel av HELE ruten som er tilbakelagt (til grønn/grå rutetegning). */
  routeProgress01: number;
  /** Sanntid (sekunder) for det AKTIVE medieklippets skjermtid. Brukes til
   *  1×-avspilling av dekodede videorammer: frameAt(frames, local01·slotSec).
   *  0 når stoppet/mellomspillet ikke har media. */
  activeMediaSlotSec?: number;
}

export interface TileRef { z: number; x: number; y: number }

export interface TileManifest {
  tiles: TileRef[];
  /** Antall zoomnivåer koreografien ble senket for å holde budsjettet. */
  zoomAdjustment: number;
  /** Effektive zoomnivåer etter justering — bruk DISSE når tidslinjen bygges
   *  for avspilling/eksport, så rendring og manifest aldri kommer i utakt. */
  driveZoom: number;
  stopZoom: number;
}

// ── Web Mercator ─────────────────────────────────────────────────────────────

export function lonToWorldX(lon: number): number {
  return (lon + 180) / 360;
}

export function latToWorldY(lat: number): number {
  // Klem til Web Mercator-gyldig område så Math.tan aldri eksploderer.
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const rad = (clamped * Math.PI) / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
}

export function toWorld(lat: number, lon: number): WorldPoint {
  return { x: lonToWorldX(lon), y: latToWorldY(lat) };
}

/** Fyller ut {z}/{x}/{y}-mal. {s}→'a' (deterministisk), {r}→'' (ingen retina). */
export function tileUrlFromTemplate(template: string, t: TileRef): string {
  return template
    .replace('{z}', String(t.z))
    .replace('{x}', String(t.x))
    .replace('{y}', String(t.y))
    .replace('{s}', 'a')
    .replace('{r}', '');
}

// ── Bezier ───────────────────────────────────────────────────────────────────

/**
 * Bygger en stilisert kurve mellom to stopp (eng-review D13: kurver, ikke
 * ruting). Kontrollpunkter forskyves vinkelrett på korden; siden alternerer
 * med legIndex så en tur ikke bøyer alle etapper samme vei. Deterministisk.
 */
export function buildLegCurve(a: WorldPoint, b: WorldPoint, legIndex: number): LegCurve {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1e-9;
  // Vinkelrett enhetsvektor; bøy ~18 % av kordelengden, alternér side.
  const side = legIndex % 2 === 0 ? 1 : -1;
  const bend = 0.18 * len * side;
  const nx = (-dy / len) * bend;
  const ny = (dx / len) * bend;
  return {
    p0: a,
    c1: { x: a.x + dx / 3 + nx, y: a.y + dy / 3 + ny },
    c2: { x: a.x + (2 * dx) / 3 + nx, y: a.y + (2 * dy) / 3 + ny },
    p1: b,
  };
}

export function bezierPoint(c: LegCurve, t: number): WorldPoint {
  const u = 1 - t;
  const w0 = u * u * u, w1 = 3 * u * u * t, w2 = 3 * u * t * t, w3 = t * t * t;
  return {
    x: w0 * c.p0.x + w1 * c.c1.x + w2 * c.c2.x + w3 * c.p1.x,
    y: w0 * c.p0.y + w1 * c.c1.y + w2 * c.c2.y + w3 * c.p1.y,
  };
}

/** Kjøretøyets retning i grader (0 = nord, med klokka) i punktet t. */
export function bezierBearingDeg(c: LegCurve, t: number): number {
  const u = 1 - t;
  const dx =
    3 * u * u * (c.c1.x - c.p0.x) + 6 * u * t * (c.c2.x - c.c1.x) + 3 * t * t * (c.p1.x - c.c2.x);
  const dy =
    3 * u * u * (c.c1.y - c.p0.y) + 6 * u * t * (c.c2.y - c.c1.y) + 3 * t * t * (c.p1.y - c.c2.y);
  // world-y peker sørover → nord er -y.
  return (Math.atan2(dx, -dy) * 180) / Math.PI;
}

// ── Vei-polylinje (ekte OSRM-rute) ───────────────────────────────────────────

/** Ett punkt langs en vei-rute i lat/lon (slik OSRM/serialisering gir det). */
export interface GeoPoint { lat: number; lon: number }

/**
 * PURE: posisjon + retning langs en polylinje ved buelengde-parameter t (0→1).
 * Interpolerer lineært mellom vei-punktene slik at kjøretøyet følger veien
 * med jevn fart (ikke jevn indeks). Retning = tangenten i punktet.
 */
export function pointAlongPolyline(pts: WorldPoint[], t01: number): { pos: WorldPoint; bearingDeg: number } {
  if (pts.length === 0) return { pos: { x: 0, y: 0 }, bearingDeg: 0 };
  if (pts.length === 1) return { pos: pts[0], bearingDeg: 0 };
  // Kumulative buelengder.
  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    seg.push(d);
    total += d;
  }
  if (total <= 0) return { pos: pts[0], bearingDeg: 0 };
  const t = t01 <= 0 ? 0 : t01 >= 1 ? 1 : t01;
  let target = t * total;
  for (let i = 0; i < seg.length; i++) {
    if (target <= seg[i] || i === seg.length - 1) {
      const f = seg[i] > 0 ? target / seg[i] : 0;
      const a = pts[i], b = pts[i + 1];
      const pos = { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
      const bearingDeg = (Math.atan2(b.x - a.x, -(b.y - a.y)) * 180) / Math.PI;
      return { pos, bearingDeg };
    }
    target -= seg[i];
  }
  const last = pts[pts.length - 1], prev = pts[pts.length - 2];
  return { pos: last, bearingDeg: (Math.atan2(last.x - prev.x, -(last.y - prev.y)) * 180) / Math.PI };
}

/** PURE: world-bbox som dekker en punktmengde. */
export function boundsOf(pts: WorldPoint[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * PURE: største zoom (mest innzoomet) der en world-bbox fortsatt får plass i
 * viewporten, med `fill`-margin. Brukes til å ramme inn nettopp den etappen —
 * ingen unødvendig utzooming (eier-feedback). Degenerert bbox → +∞ (kalleren
 * klemmer mot stopZoom).
 */
export function zoomToFit(
  b: { minX: number; minY: number; maxX: number; maxY: number },
  vp: ViewportPx, tileSize = 256, fill = 0.6,
): number {
  const dwx = Math.max(0, b.maxX - b.minX);
  const dwy = Math.max(0, b.maxY - b.minY);
  const zx = dwx > 0 ? Math.log2((vp.width * fill) / (tileSize * dwx)) : Infinity;
  const zy = dwy > 0 ? Math.log2((vp.height * fill) / (tileSize * dwy)) : Infinity;
  return Math.min(zx, zy);
}

/**
 * PURE: zoom for én kjøreetappe. Rammer inn etappens rute (eller endepunktene
 * hvis ingen rute) og klemmer til [driveZoom, stopZoom] slik at korte hopp
 * knapt zoomer ut og lange etapper aldri zoomer lenger ut enn budsjett-gulvet.
 * Uten viewport → driveZoom (bakoverkompatibelt: gammel fast utzooming).
 */
export function legFitZoom(
  path: WorldPoint[], vp: ViewportPx | undefined, driveZoom: number, stopZoom: number,
): number {
  if (!vp || path.length < 2) return driveZoom;
  const fit = zoomToFit(boundsOf(path), vp);
  return Math.max(driveZoom, Math.min(stopZoom, fit));
}

// ── Timeline ─────────────────────────────────────────────────────────────────

export interface TimelineOptions {
  minTotalSec?: number;   // default 15
  maxTotalSec?: number;   // default 30
  stopSecBase?: number;   // ønsket pause per stopp før klemming (default 3)
  driveSecBase?: number;  // ønsket kjøretid per etappe før klemming (default 2.5)
  driveZoom?: number;     // default 6.5
  stopZoom?: number;      // default 11
  /** Viewport for per-etappe fit-zoom (utelatt → fast driveZoom, bakoverkompat). */
  viewport?: ViewportPx;
}

/** Kortslutning for degenererte reiser: replay krever ≥ 2 stopp (eng-review). */
export function isViableTrip(spec: TripSpec): boolean {
  return spec.stops.length >= 2;
}

export interface TripSpecBuildResult {
  spec: TripSpec;
  /** Besøk utelatt pga. manglende/ugyldige koordinater (vises i UI, premiss 2). */
  skippedNoCoords: number;
}

/**
 * Bygger TripSpec fra brukerens (periodfiltrerte) besøk.
 * - Kronologisk sortering på visitedAt. Datakilden kan være visits-settet (ett stopp
 *   per plass) ELLER checkin_log-opphold via getReplayVisits (ett stopp per opphold →
 *   atskilte gjenbesøk på samme plass blir separate stopp). buildTripSpec bryr seg ikke
 *   om hvilken — den mapper hver SpotVisit til ett stopp.
 * - Besøk uten gyldige koordinater utelates og telles (UI-hint).
 * - Foto-kjede (eng-review, «besøk uten bilde»): visit.photoUrl →
 *   photoBySpotId (f.eks. spots[].photoUrls[0]) → intet (kort uten polaroid).
 */
export function buildTripSpec(
  visits: readonly SpotVisit[],
  photoBySpotId?: ReadonlyMap<string, string>,
): TripSpecBuildResult {
  const hasCoords = (v: SpotVisit) =>
    Number.isFinite(v.latitude) && Number.isFinite(v.longitude) &&
    Math.abs(v.latitude) <= 90 && Math.abs(v.longitude) <= 180 &&
    !(v.latitude === 0 && v.longitude === 0);

  const usable = visits.filter(hasCoords);
  const stops: ReplayStop[] = [...usable]
    .sort((a, b) => a.visitedAt - b.visitedAt)
    .map(v => ({
      spotId: v.spotId,
      name: v.spotName,
      latitude: v.latitude,
      longitude: v.longitude,
      visitedAt: v.visitedAt,
      ...(typeof v.checkedOutAt === 'number' ? { checkedOutAt: v.checkedOutAt } : {}),
      ...(v.county ? { county: v.county } : {}),
      ...((v.photoUrl || photoBySpotId?.get(v.spotId))
        ? { photoUrl: v.photoUrl || photoBySpotId!.get(v.spotId)! }
        : {}),
    }));

  return { spec: { stops }, skippedNoCoords: visits.length - usable.length };
}

/**
 * Bygger tidslinjen: stopp → kjør → stopp → … Total lengde klemmes til
 * [minTotalSec, maxTotalSec] ved å skalere alle segmentvarigheter likt
 * (dynamisk tempo — eng-review D8/designdok).
 */
export function buildTimeline(spec: TripSpec, opts: TimelineOptions = {}): ReplayTimeline {
  if (!isViableTrip(spec)) throw new Error('ReplayTimeline krever minst 2 stopp');
  const {
    minTotalSec = 15, maxTotalSec = 30,
    stopSecBase = 3, driveSecBase = 2.5,
    driveZoom = 6.5, stopZoom = 11, viewport,
  } = opts;

  const n = spec.stops.length;
  // Per-innslag skjermtid. Et VIDEOKLIPP trenger nok tid til å spille av i
  // 1× (eier-funn: «altfor fort» — klipp blinket forbi fordi de delte et lite
  // stopp-slot med samme vekt som et foto). Derfor får hvert klipp sitt eget,
  // beskyttede slot ~ klippets lengde (kappet), mens bilder deler 2,6 s.
  const PHOTO_SLOT_SEC = 2.6;
  const VIDEO_FALLBACK_SEC = 5; // brukt bare når klippets faktiske lengde ikke er kjent ennå
  // Videoklippets slot = dets FAKTISKE lengde (kappet 2–6 s), så det spilles helt
  // gjennom i 1× og KUTTER — ikke fryser på siste ramme (eier-funn: «stopper
  // videoer fremfor å bare spille»). Krever at durationSec er satt (dekodet) FØR
  // tidslinjen bygges.
  const mediaSec = (items?: StopMedia[]) =>
    (items ?? []).reduce((a, m) => a + (m.kind === 'video'
      ? Math.min(6, Math.max(2, m.durationSec ?? VIDEO_FALLBACK_SEC))
      : PHOTO_SLOT_SEC), 0);
  const hasVideo = (items?: StopMedia[]) => !!items?.some(m => m.kind === 'video');

  // Rå (uskalert) varighet per stopp: media-stopp = sum av innslag-slots;
  // ellers kart-opphold (mindre for bildeløse stopp — eier-funn).
  const stopRawSec = (i: number) => {
    const s = spec.stops[i];
    if (s.media?.length) return mediaSec(s.media);
    return stopSecBase * (s.photoUrl ? 1 : 0.45);
  };
  const interludeFor = (i: number) => spec.interludes?.find(x => x.afterStopIndex === i);
  const interludeRawSec = (i: number) => mediaSec(interludeFor(i)?.media);

  // Del tiden i FAST (video-slots — spilles alltid i 1×, aldri komprimert av
  // tempo-klemmen) og SKALERBAR (kart-opphold, foto-slots, kjøring). Tempoet
  // skalerer kun det skalerbare; video-segmenter beholder sin rå varighet.
  const stopHasVideo = (i: number) => hasVideo(spec.stops[i].media);
  const interludeHasVideo = (i: number) => hasVideo(interludeFor(i)?.media);
  let fixedSec = 0, scalableRaw = 0;
  for (let i = 0; i < n; i++) {
    if (stopHasVideo(i)) fixedSec += stopRawSec(i); else scalableRaw += stopRawSec(i);
    if (i < n - 1) {
      scalableRaw += driveSecBase;
      if (interludeHasVideo(i)) fixedSec += interludeRawSec(i); else scalableRaw += interludeRawSec(i);
    }
  }
  // Sikt mot [min,max] TOTALT, men aldri komprimer video-tiden bort. Skaler
  // bare den skalerbare resten inn i den gjenværende plassen (med et gulv/tak
  // så kjøring/opphold verken forsvinner eller drar ut).
  const targetTotal = Math.min(maxTotalSec, Math.max(minTotalSec, scalableRaw + fixedSec));
  const scalableTarget = Math.max(0, targetTotal - fixedSec);
  const rawScale = scalableRaw > 0 ? scalableTarget / scalableRaw : 1;
  // Uten video: ren [min,max]-klemme (rawScale treffer targetTotal presist).
  // Med video: gulv så kjøring/kart-opphold ikke forsvinner helt mellom klippene
  // (video-tiden kan skyve totalen forbi maxTotalSec — det er meningen).
  const scale = fixedSec > 0 ? Math.max(0.5, rawScale) : rawScale;
  const driveSec = driveSecBase * scale;

  const segments: TimelineSegment[] = [];
  let tCursor = 0;
  for (let i = 0; i < n; i++) {
    // Video-stopp: rå varighet (uskalert). Ellers skalert kart-/foto-opphold.
    const stopSec = stopHasVideo(i) ? stopRawSec(i) : stopRawSec(i) * scale;
    segments.push({ kind: 'stop', startSec: tCursor, durationSec: stopSec, stopIndex: i });
    tCursor += stopSec;
    if (i < n - 1) {
      const a = toWorld(spec.stops[i].latitude, spec.stops[i].longitude);
      const b = toWorld(spec.stops[i + 1].latitude, spec.stops[i + 1].longitude);
      // Ekte vei-rute for etappen hvis hentet; ellers Bezier-«flukt» (off-road).
      const geo = spec.legRoutes?.[i];
      const routePath = geo && geo.length >= 2 ? geo.map(p => toWorld(p.lat, p.lon)) : undefined;
      // Fit-zoom rammer inn nettopp denne etappen (rute-bbox el. endepunktene).
      const fit = legFitZoom(routePath ?? [a, b], viewport, driveZoom, stopZoom);
      segments.push({
        kind: 'drive', startSec: tCursor, durationSec: driveSec, stopIndex: i,
        curve: buildLegCurve(a, b, i), routePath, driveZoomFit: fit,
      });
      tCursor += driveSec;
      // Mellomspill: medier tatt underveis på etappen vises ETTER kjøringen,
      // før neste stopp — «vi kjørte, se hva vi så på veien».
      const inter = interludeFor(i);
      if (inter?.media.length) {
        const dur = interludeHasVideo(i) ? interludeRawSec(i) : interludeRawSec(i) * scale;
        segments.push({
          kind: 'interlude', startSec: tCursor, durationSec: dur, stopIndex: i,
          interludeMedia: inter.media, driveZoomFit: fit,
        });
        tCursor += dur;
      }
    }
  }
  return { spec, segments, totalSec: tCursor, driveZoom, stopZoom };
}

/** Ett videoklipps absolutte tidsvindu i filmen (til lyd-miksing i eksport). */
export interface VideoClipWindow { url: string; startSec: number; slotSec: number }

/**
 * PURE: alle videoklipp med sitt absolutte tidsvindu (start + skjermtid) i
 * tidslinjen. Medier på et stopp/mellomspill deler segmentets varighet likt
 * (samme som mediaAt/avspilling), så klipp i deler slottet = seg.dur / antall.
 */
export function collectVideoClips(tl: ReplayTimeline): VideoClipWindow[] {
  const out: VideoClipWindow[] = [];
  for (const seg of tl.segments) {
    const media = seg.kind === 'stop' ? tl.spec.stops[seg.stopIndex].media
      : seg.kind === 'interlude' ? seg.interludeMedia
      : undefined;
    if (!media?.length) continue;
    const slot = seg.durationSec / media.length;
    media.forEach((m, i) => {
      if (m.kind === 'video') out.push({ url: m.url, startSec: seg.startSec + i * slot, slotSec: slot });
    });
  }
  return out;
}

/** Myk easing for kamera/kort (easeInOutCubic). */
function ease(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Deterministisk frame-tilstand for tidspunkt tSec. Ingen klokke, ingen
 * tilfeldighet — samme (timeline, tSec) gir alltid samme frame (D11-krav
 * for offline WebCodecs-rendring).
 */
export function frameState(tl: ReplayTimeline, tSec: number): FrameState {
  const t = Math.max(0, Math.min(tl.totalSec, tSec));
  let seg = tl.segments[tl.segments.length - 1];
  for (const s of tl.segments) {
    if (t < s.startSec + s.durationSec) { seg = s; break; }
  }
  const local = seg.durationSec > 0 ? (t - seg.startSec) / seg.durationSec : 1;
  const stops = tl.spec.stops;
  const worldOf = (i: number) => toWorld(stops[i].latitude, stops[i].longitude);

  const driveSegs = tl.segments.filter(s => s.kind === 'drive');
  const legCount = Math.max(1, driveSegs.length);

  if (seg.kind === 'stop') {
    const pos = worldOf(seg.stopIndex);
    // Kortet fader inn første 20 % og ut siste 15 % av pausen.
    const fadeIn = Math.min(1, local / 0.2);
    const fadeOut = Math.min(1, (1 - local) / 0.15);
    const stopMediaCount = stops[seg.stopIndex].media?.length ?? 0;
    // Ferdige etapper = stopIndex (alle etapper FØR dette stoppet).
    return {
      center: pos,
      zoom: tl.stopZoom,
      anchorZoom: Math.max(0, Math.round(tl.stopZoom)),
      vehicle: { pos, bearingDeg: 0, visible: true },
      activeStopIndex: seg.stopIndex,
      stopCardOpacity: Math.max(0, Math.min(fadeIn, fadeOut)),
      stopLocalProgress01: local,
      progress01: tl.totalSec > 0 ? t / tl.totalSec : 1,
      routeProgress01: seg.stopIndex / legCount,
      activeMediaSlotSec: stopMediaCount > 0 ? seg.durationSec / stopMediaCount : 0,
    };
  }

  if (seg.kind === 'interlude') {
    // Kamera holder ved neste stopp (dit etappen førte) på etappens fit-zoom —
    // mediet fyller uansett skjermen; kartet er bare bakteppe i overgangene.
    const nextPos = worldOf(Math.min(seg.stopIndex + 1, stops.length - 1));
    const zoom = seg.driveZoomFit ?? tl.driveZoom;
    return {
      center: nextPos,
      zoom,
      anchorZoom: Math.max(0, Math.round(zoom)),
      vehicle: { pos: nextPos, bearingDeg: 0, visible: false },
      activeStopIndex: null,
      stopCardOpacity: 0,
      stopLocalProgress01: 0,
      interlude: { media: seg.interludeMedia ?? [], local01: local },
      progress01: tl.totalSec > 0 ? t / tl.totalSec : 1,
      routeProgress01: (seg.stopIndex + 1) / legCount,
      activeMediaSlotSec: (seg.interludeMedia?.length ?? 0) > 0 ? seg.durationSec / seg.interludeMedia!.length : 0,
    };
  }

  // drive-segment
  const e = ease(local);
  // Følg den faktiske veien når vi har en rute; ellers Bezier-fallback.
  let pos: WorldPoint, bearing: number;
  if (seg.routePath && seg.routePath.length >= 2) {
    const r = pointAlongPolyline(seg.routePath, e);
    pos = r.pos; bearing = r.bearingDeg;
  } else {
    pos = bezierPoint(seg.curve!, e);
    bearing = bezierBearingDeg(seg.curve!, e);
  }
  // Kamera følger bilen; zoomer ut mot midten av etappen KUN til etappens
  // fit-zoom (aldri lenger ut enn ruten krever) og inn mot endene.
  const fitZoom = seg.driveZoomFit ?? tl.driveZoom;
  const zoomDip = Math.sin(local * Math.PI); // 0→1→0
  const zoom = tl.stopZoom + (fitZoom - tl.stopZoom) * zoomDip;
  return {
    center: pos,
    zoom,
    anchorZoom: Math.max(0, Math.round(fitZoom)),
    vehicle: { pos, bearingDeg: bearing, visible: true },
    activeStopIndex: null,
    stopCardOpacity: 0,
    stopLocalProgress01: 0,
    progress01: tl.totalSec > 0 ? t / tl.totalSec : 1,
    routeProgress01: (seg.stopIndex + e) / legCount,
  };
}

/**
 * Hvilket medieinnslag (indeks i stop.media) som vises ved gitt lokal
 * stopp-fremdrift, med crossfade-informasjon. Bilder deler pausen likt;
 * en video får hele pausen alene hvis den er eneste innslag.
 */
export function mediaAt(mediaCount: number, stopLocal01: number): { index: number; local01: number } {
  if (mediaCount <= 0) return { index: -1, local01: 0 };
  const clamped = Math.max(0, Math.min(0.9999, stopLocal01));
  const slot = clamped * mediaCount;
  return { index: Math.floor(slot), local01: slot - Math.floor(slot) };
}

/** Sakte zoom/pan (Ken Burns) for stillbilder — gjør statiske foto levende. */
export interface KenBurnsTransform {
  /** Overscan-zoom (>= 1). Bildet dekker alltid frame-n så lenge scale >= 1. */
  scale: number;
  /** Pan innenfor overscan-rommet, normalisert til [-1, 1]. */
  panX: number;
  panY: number;
}

// Deterministiske pan/zoom-mønstre. `seed` (medie-indeks) velger mønster slik
// at påfølgende bilder ikke beveger seg likt; ingen Math.random → ren + testbar.
const KB_PATTERNS: ReadonlyArray<{ z0: number; z1: number; x0: number; y0: number; x1: number; y1: number }> = [
  { z0: 1.02, z1: 1.14, x0: -0.6, y0: -0.35, x1: 0.5, y1: 0.35 }, // zoom inn, diagonal ↘
  { z0: 1.14, z1: 1.03, x0: 0.5, y0: 0.45, x1: -0.5, y1: -0.3 },  // zoom ut, diagonal ↖
  { z0: 1.04, z1: 1.12, x0: 0.6, y0: -0.3, x1: -0.5, y1: 0.3 },   // zoom inn, høyre→venstre
  { z0: 1.10, z1: 1.02, x0: -0.5, y0: 0.4, x1: 0.45, y1: -0.4 },  // zoom ut, venstre→høyre
  // Flere mønstre (eier-funn: for lite variasjon) — sterkere zoom + rene akser.
  { z0: 1.16, z1: 1.02, x0: 0, y0: -0.55, x1: 0, y1: 0.5 },       // zoom ut, pan ↓ (vertikal)
  { z0: 1.02, z1: 1.18, x0: 0, y0: 0.5, x1: 0, y1: -0.5 },        // zoom inn, pan ↑ (vertikal)
  { z0: 1.03, z1: 1.20, x0: -0.55, y0: 0.3, x1: 0.55, y1: -0.35 },// kraftig zoom inn, diagonal ↗
  { z0: 1.18, z1: 1.04, x0: 0.55, y0: -0.45, x1: -0.5, y1: 0.45 },// kraftig zoom ut, diagonal ↙
];

/**
 * PURE: Ken Burns-transform for et stillbilde. `local01` (0→1) er bildets
 * lokale fremdrift over sin skjermtid; ease-in-out gir mjuk start/stopp.
 */
export function kenBurnsTransform(seed: number, local01: number): KenBurnsTransform {
  const n = KB_PATTERNS.length;
  const p = KB_PATTERNS[((Math.trunc(seed) % n) + n) % n];
  const t = local01 <= 0 ? 0 : local01 >= 1 ? 1 : local01;
  const e = t * t * (3 - 2 * t); // smoothstep
  return {
    scale: p.z0 + (p.z1 - p.z0) * e,
    panX: p.x0 + (p.x1 - p.x0) * e,
    panY: p.y0 + (p.y1 - p.y0) * e,
  };
}

/** Scene-overgang: alpha-crossfade + en liten retnings-«push» ved inngang. */
export interface SceneTransition {
  /** 0 = usynlig (kartet bak vises), 1 = full dekning. */
  alpha: number;
  /** Ekstra forskyvning ved inngang, normalisert til frame (0 når scenen er satt). */
  slideX01: number;
  slideY01: number;
}

// Rene akser først (indeks 0–3 — testet: seed 2 = ren y), så diagonaler (4–7)
// for mer variasjon i overgangs-«pushen» (eier-funn).
const SLIDE_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [0.7, 0.7], [-0.7, 0.7], [0.7, -0.7], [-0.7, -0.7],
];

/**
 * PURE: overgang for en medie-scene. `local01` (0→1) er scenens lokale
 * fremdrift; `windowFrac` er hvor stor del av start/slutt som crossfader.
 * Alpha stiger 0→1 i inngangsvinduet og faller 1→0 i utgangsvinduet slik at
 * kartet bak crossfader inn og ut. En liten «push» (fra `seed`-retning) gir
 * en koreografert inngang uten et helt overgangsbibliotek.
 *
 * `fadeIn`/`fadeOut` styrer om kartet skal vises i inn-/utgangen. For medier
 * MIDT i en plass' sekvens settes begge av (=1) slik at klippene kuttes rett
 * over i hverandre — kartet skal kun avsløres når man ANKOMMER en ny innsjekk
 * (eier-krav: kartet indikerer hvor bildene/videoene er fra, ikke mellom hvert
 * klipp på samme sted).
 */
export function sceneTransition(
  seed: number, local01: number, windowFrac = 0.14, fadeIn = true, fadeOut = true,
): SceneTransition {
  const t = local01 <= 0 ? 0 : local01 >= 1 ? 1 : local01;
  const w = windowFrac <= 0 ? 1e-6 : windowFrac;
  const inA = fadeIn ? Math.min(1, t / w) : 1;
  const outA = fadeOut ? Math.min(1, (1 - t) / w) : 1;
  const alpha = Math.max(0, Math.min(inA, outA));
  const n = SLIDE_DIRS.length;
  const d = SLIDE_DIRS[((Math.trunc(seed) % n) + n) % n];
  const push = (1 - inA) * 0.06; // maks 6 % forskyvning helt i starten, → 0 straks scenen er inne
  return { alpha, slideX01: d[0] * push, slideY01: d[1] * push };
}

/**
 * PURE: rask «pop-in» zoom-punch når et medieklipp starter (eier-ønske: mer
 * dynamiske overganger mellom bilder/videoer — før var det hardt kutt mellom
 * klipp på samme sted). Returverdien MULTIPLISERES på cover/Ken Burns-skalaen,
 * så den kun øker overskann (aldri svarte kanter). Starter på 1+mag og eases ut
 * (easeOutCubic) til 1.0 over `windowFrac` av klippets tid. `seed` varierer
 * styrken litt mellom klipp. `local01` (0→1) er klippets lokale fremdrift.
 */
export function mediaEntryScale(seed: number, local01: number, windowFrac = 0.2): number {
  const w = windowFrac <= 0 ? 1e-6 : windowFrac;
  const entry = local01 <= 0 ? 0 : Math.min(1, local01 / w);
  const ease = 1 - Math.pow(1 - entry, 3); // easeOutCubic
  const mag = 0.12 + 0.05 * ((((Math.trunc(seed) % 3) + 3) % 3)); // 0.12 / 0.17 / 0.22
  return 1 + (1 - ease) * mag;
}

export interface MediaMotion { scale: number; slideX01: number; slideY01: number; }

/**
 * PURE: inngangs-bevegelse for et medieklipp, valgt av stilpakkens overgangs-
 * familie (Fase 1 «CapCut-stilpakker»). Alle varianter øker KUN overskann → aldri
 * svarte kanter. `punch` = pop-in zoom; `whip` = pop-in + kjapp horisontal glid
 * (innenfor overskann-budsjettet); `driftzoom` = sakte kontinuerlig zoom (ingen
 * pop, rolige pakker). `seed` varierer retning/styrke; `local01` klippfremdrift.
 */
export function mediaEntryMotion(
  transition: string, seed: number, local01: number, windowFrac = 0.2,
): MediaMotion {
  const t = local01 <= 0 ? 0 : local01 >= 1 ? 1 : local01;
  if (transition === 'driftzoom') {
    return { scale: 1.04 + 0.12 * t, slideX01: 0, slideY01: 0 };
  }
  const scale = mediaEntryScale(seed, local01, windowFrac);
  if (transition === 'whip') {
    const w = windowFrac <= 0 ? 1e-6 : windowFrac;
    const entry = local01 <= 0 ? 0 : Math.min(1, local01 / w);
    const rem = 1 - (1 - Math.pow(1 - entry, 3)); // easeOutCubic-rest
    const n = SLIDE_DIRS.length;
    const d = SLIDE_DIRS[((Math.trunc(seed) % n) + n) % n];
    return { scale, slideX01: d[0] * rem * 0.06, slideY01: 0 };
  }
  return { scale, slideX01: 0, slideY01: 0 }; // 'punch' (default)
}

/**
 * PURE: beat-puls (Fase 2 beat-synk) — en liten, rask «pumpe» i skala på hver
 * takt, avledet av pakkens BPM. Gir CapCut-følelsen av at filmen beveger seg med
 * musikken. Returnerer en skala-multiplikator ≥ 1 (øker kun overskann). `amp` er
 * maks utslag (subtilt, ~2–4 %). bpm ≤ 0 → 1 (ingen puls).
 */
export function beatPulse(tSec: number, bpm: number, amp = 0.03): number {
  if (!bpm || bpm <= 0) return 1;
  const beatSec = 60 / bpm;
  const phase = ((tSec % beatSec) + beatSec) % beatSec / beatSec; // 0..1 inn i takten
  return 1 + amp * Math.pow(1 - phase, 4); // spiss på taktslaget, faller raskt
}

/**
 * PURE: plasserer brukerens medier kronologisk i reisen (CapCut-flyten:
 * legg alt til på forhånd, motoren fletter). Tidsstempel (atMs, typisk
 * File.lastModified) avgjør: innenfor et opphold → på stoppet; mellom to
 * stopp → mellomspill etter etappen; før første/ukjent → første stopp;
 * etter siste → siste stopp. Returnerer NYE stops + interludes.
 */
export function placeTripMedia(
  stops: ReplayStop[],
  items: Array<{ media: StopMedia; atMs?: number }>,
): { stops: ReplayStop[]; interludes: TripInterlude[] } {
  const outStops = stops.map(s => ({ ...s, media: [...(s.media ?? [])] }));
  const interMap = new Map<number, StopMedia[]>();
  const stayEnd = (s: ReplayStop) => Math.max(s.checkedOutAt ?? s.visitedAt, s.visitedAt);
  const validDate = (at?: number): at is number =>
    typeof at === 'number' && Number.isFinite(at) && at >= 946684800000 /* før år 2000 = utroverdig */;

  const pushInterlude = (afterStopIndex: number, media: StopMedia) => {
    const list = interMap.get(afterStopIndex) ?? [];
    list.push(media);
    interMap.set(afterStopIndex, list);
  };

  // Innslag UTEN pålitelig opptaksdato (eier-funn: File.lastModified ≈ kopitid →
  // ALLE klipp havnet på samme stopp og blinket forbi). I stedet for å dumpe dem
  // på stopp 0, sprer vi dem JEVNT utover reisen (mellomspill mellom stoppene),
  // så de vises spredt gjennom filmen med hvert sitt slot.
  const undated: StopMedia[] = [];

  for (const item of items) {
    const at = item.atMs;
    if (outStops.length === 0) break;
    if (!validDate(at)) { undated.push(item.media); continue; }
    let placed = false;
    for (let i = 0; i < outStops.length; i++) {
      const s = outStops[i];
      if (at >= s.visitedAt && at <= stayEnd(s)) {
        s.media!.push(item.media);
        placed = true;
        break;
      }
      const next = outStops[i + 1];
      if (next && at > stayEnd(s) && at < next.visitedAt) {
        pushInterlude(i, item.media);
        placed = true;
        break;
      }
    }
    if (!placed) {
      // Før første → første stopp; etter siste → siste stopp.
      const target = at < outStops[0].visitedAt ? outStops[0] : outStops[outStops.length - 1];
      target.media!.push(item.media);
    }
  }

  // Fordel de udaterte jevnt over etappene (round-robin over afterStopIndex
  // 0..n-2). Bare ett stopp → alle på stopp 0.
  const legs = Math.max(1, outStops.length - 1);
  undated.forEach((media, k) => {
    if (outStops.length <= 1) { outStops[0].media!.push(media); return; }
    pushInterlude(k % legs, media);
  });

  const interludes: TripInterlude[] = [...interMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([afterStopIndex, media]) => ({ afterStopIndex, media }));
  const cleanedStops = outStops.map(s => (s.media!.length ? s : (({ media, ...rest }) => rest)(s) as ReplayStop));
  return { stops: cleanedStops, interludes };
}

// ── Flis-manifest (slippy-matte) ─────────────────────────────────────────────

export interface ViewportPx { width: number; height: number }

/**
 * Fliser på gitt anker-nivå (default round(zoom)) som dekker viewporten rundt
 * center. Kalleren tegner dem skalert med 2^(zoom - anchorZ). Bruk
 * FrameState.anchorZoom som anker — se kommentaren der for hvorfor fliser
 * aldri hentes på mellomliggende zoomnivåer.
 */
export function tilesForView(center: WorldPoint, zoom: number, vp: ViewportPx, tileSize = 256, anchorZ?: number): TileRef[] {
  const zInt = Math.max(0, anchorZ ?? Math.round(zoom));
  const n = Math.pow(2, zInt);
  const scale = Math.pow(2, zoom - zInt); // px per world-flis relativt tileSize
  const worldPerPxX = 1 / (n * tileSize * scale);
  const halfW = (vp.width / 2) * worldPerPxX;
  const halfH = (vp.height / 2) * worldPerPxX;
  const minX = Math.floor((center.x - halfW) * n);
  const maxX = Math.floor((center.x + halfW) * n);
  const minY = Math.floor((center.y - halfH) * n);
  const maxY = Math.floor((center.y + halfH) * n);
  const tiles: TileRef[] = [];
  for (let x = minX; x <= maxX; x++) {
    for (let y = Math.max(0, minY); y <= Math.min(n - 1, maxY); y++) {
      // Wrap x (antimeridian) — relevant i teorien, gratis å håndtere.
      const wx = ((x % n) + n) % n;
      tiles.push({ z: zInt, x: wx, y });
    }
  }
  return tiles;
}

export interface ManifestOptions {
  /** Maks unike fliser. Default 260 — kalibrert empirisk i Fase 0-spiken:
   *  en normal 3-stopps tur trenger ~216 med segment-anker (eng-review D8
   *  sa «~200» som eksempel; 260 gir normal-turen headroom uten senking). */
  budget?: number;
  sampleHz?: number;  // hvor ofte tidslinjen samples (default 4)
  maxZoomDrops?: number; // hvor mange nivåer koreografien kan senkes (default 8)
}

/**
 * Samler alle fliser tidslinjen trenger. Overskrides budsjettet, senkes
 * drive- og stop-zoom ett nivå og det prøves på nytt (adaptiv zoom, D8).
 * Ren funksjon: gitt samme input → samme manifest.
 */
export function buildTileManifest(
  spec: TripSpec,
  vp: ViewportPx,
  tlOpts: TimelineOptions = {},
  opts: ManifestOptions = {},
): TileManifest {
  const { budget = 260, sampleHz = 4, maxZoomDrops = 8 } = opts;

  for (let drop = 0; drop <= maxZoomDrops; drop++) {
    // Zoom-gulv 1: under det finnes knapt fliser å spare, og kartet mister mening.
    const driveZoom = Math.max(1, (tlOpts.driveZoom ?? 6.5) - drop);
    const stopZoom = Math.max(1, (tlOpts.stopZoom ?? 11) - drop);
    const tl = buildTimeline(spec, { ...tlOpts, driveZoom, stopZoom });
    const seen = new Set<string>();
    const tiles: TileRef[] = [];
    const steps = Math.max(2, Math.ceil(tl.totalSec * sampleHz));
    for (let i = 0; i <= steps; i++) {
      const st = frameState(tl, (i / steps) * tl.totalSec);
      // frameState eier ankeret — manifest og tegning er i lås per definisjon.
      for (const tile of tilesForView(st.center, st.zoom, vp, 256, st.anchorZoom)) {
        const key = `${tile.z}/${tile.x}/${tile.y}`;
        if (!seen.has(key)) { seen.add(key); tiles.push(tile); }
      }
    }
    if (tiles.length <= budget || drop === maxZoomDrops) {
      return { tiles, zoomAdjustment: drop, driveZoom, stopZoom };
    }
  }
  // Uoppnåelig (loopen returnerer alltid), men TS vil ha en hale.
  return { tiles: [], zoomAdjustment: maxZoomDrops, driveZoom: 1, stopZoom: 1 };
}
